
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { Link } from 'react-router-dom';
import Header from './components/Header';

interface RetentionData {
  id: number;
  employee: string;
  collections: string;
  policies: number | null;
  roadsides: number;
  total_fees_collected: string;
  late_payments: string;
  monthly_payments: string;
  renewals: string;
  reinstatements: string;
  endorsements: string;
  add_veh_endo: string;
  dialers: number;
  [key: string]: any;
}

// Profile photos mapping - same as other pages for consistency
const profilePhotos: { [key: string]: string } = {};

// Function to get profile photo
const getProfilePhoto = (employeeName: string): string | null => {
  if (!employeeName) return null;
  const key = employeeName.toLowerCase().trim();
  if (profilePhotos[key]) return profilePhotos[key];
  const initials = employeeName.substring(0, 2).toUpperCase();
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(
    initials
  )}&background=3b82f6&color=ffffff&size=128`;
};

// USA Retention Team Members
const USA_RETENTION_TEAM = ['fernando', 'jenny', 'yesenia', 'paul', 'yulisa'];

// Philippines Retention Team Members
const PH_RETENTION_TEAM = [
  'arlene',
  'pauli',
  'justine',
  'clairen',
  'honey',
  'berlyn',
  'emet',
  'jerald',
  'valerina',
  'jezel',
  'ivan',
];

// Helper function to check if employee is in USA team
const isUSATeamMember = (employeeName: string): boolean => {
  if (!employeeName || typeof employeeName !== 'string') return false;
  const nameLower = employeeName.toLowerCase().trim();
  const firstName = nameLower.split(' ')[0];
  return (
    USA_RETENTION_TEAM.includes(nameLower) ||
    USA_RETENTION_TEAM.includes(firstName)
  );
};

// Helper function to check if employee is in PH team
const isPHTeamMember = (employeeName: string): boolean => {
  if (!employeeName || typeof employeeName !== 'string') return false;
  const nameLower = employeeName.toLowerCase().trim();
  const firstName = nameLower.split(' ')[0];
  return (
    PH_RETENTION_TEAM.includes(nameLower) ||
    PH_RETENTION_TEAM.includes(firstName)
  );
};

// Ranking Table Component
function RankingTable({
  employees,
  title,
  teamFilter,
}: {
  employees: any[];
  title: string;
  teamFilter: 'USA' | 'PH';
}) {
  // Filter employees by team
  const teamEmployees = employees.filter((emp) => {
    const empName = emp.employee || emp.name || emp.agent_name || emp.sales_agent || 'Unknown';
    if (teamFilter === 'USA') {
      return isUSATeamMember(empName);
    } else {
      return isPHTeamMember(empName);
    }
  });

  // Create comprehensive ranking data with different sorting for each team
  const createComprehensiveRanking = (employees: any[]) => {
    const processedEmployees = [...employees].map((emp) => {
      const name = emp.employee || emp.name || emp.agent_name || emp.sales_agent || 'Unknown';

      // Parse numeric values
      const collections =
        typeof emp.collections === 'string'
          ? parseFloat(emp.collections.replace(/[$,]/g, '')) || 0
          : emp.collections || 0;

      const policies =
        typeof emp.policies === 'string'
          ? parseInt(emp.policies.replace(/[^0-9]/g, '')) || 0
          : emp.policies || 0;

      const roadsides =
        typeof emp.roadsides === 'string'
          ? parseInt(emp.roadsides.replace(/[^0-9]/g, '')) || 0
          : emp.roadsides || 0;

      const totalFees =
        typeof emp.total_fees_collected === 'string'
          ? parseFloat(emp.total_fees_collected.replace(/[$,]/g, '')) || 0
          : emp.total_fees_collected || 0;

      const dialers =
        typeof emp.dialers === 'string'
          ? parseInt(emp.dialers.replace(/[^0-9]/g, '')) || 0
          : emp.dialers || 0;

      return {
        name,
        collections,
        policies,
        roadsides,
        totalFees,
        dialers,
        photo: getProfilePhoto(name),
      };
    });

    // Sort based on team filter
    if (teamFilter === 'PH') {
      // PH Ranking: 1) Dialers, 2) Total Fees, 3) Total Roadsides, 4) Collections
      return processedEmployees.sort((a, b) => {
        if (b.dialers !== a.dialers) return b.dialers - a.dialers;
        if (b.totalFees !== a.totalFees) return b.totalFees - a.totalFees;
        if (b.roadsides !== a.roadsides) return b.roadsides - a.roadsides;
        return b.collections - a.collections;
      });
    } else {
      // USA Ranking: 1) Policy Count, 2) Roadside, 3) Collections
      return processedEmployees.sort((a, b) => {
        if (b.policies !== a.policies) return b.policies - a.policies;
        if (b.roadsides !== a.roadsides) return b.roadsides - a.roadsides;
        return b.collections - a.collections;
      });
    }
  };

  const rankedEmployees = createComprehensiveRanking(teamEmployees);

  if (rankedEmployees.length === 0) {
    return null;
  }

  const getRankIcon = (rank: number) => {
    if (rank === 1) return '🏆';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return null;
  };

  const maxCollections = Math.max(...rankedEmployees.map((e) => e.collections), 1);
  const maxPolicies = Math.max(...rankedEmployees.map((e) => e.policies), 1);
  const maxRoadsides = Math.max(...rankedEmployees.map((e) => e.roadsides), 1);
  const maxTotalFees = Math.max(...rankedEmployees.map((e) => e.totalFees), 1);
  const maxDialers = Math.max(...rankedEmployees.map((e) => e.dialers), 1);

  // Get ranking criteria text based on team
  const getRankingCriteria = () => {
    if (teamFilter === 'PH') {
      return 'Ranked by: 1) Dialers, 2) Total Fees, 3) Total Roadsides, 4) Collections';
    } else {
      return 'Ranked by: 1) Policy Count, 2) Roadside, 3) Collections';
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-white border-b border-gray-200">
        <h3 className="text-base font-bold text-gray-900">{title}</h3>
        <p className="text-xs text-gray-600 mt-1">{getRankingCriteria()}</p>
        <p className="text-xs text-blue-600 font-medium mt-1">
          Showing {rankedEmployees.length} {teamFilter} employees
        </p>
      </div>

      {/* List Items */}
      <div className="divide-y divide-gray-200">
        {rankedEmployees.map((employee, index) => {
          const rank = index + 1;
          const isTopThree = rank <= 3;
          const rankIcon = getRankIcon(rank);
          
          return (
            <div 
              key={index}
              className={`px-4 py-3 ${isTopThree ? 'bg-yellow-50' : 'bg-white'}`}
            >
              {/* Agent Info Row */}
              <div className="flex items-center mb-2">
                {/* Rank Badge */}
                <div className="flex-shrink-0 mr-2">
                  {rankIcon ? (
                    <span className="text-xl">{rankIcon}</span>
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-xs font-bold text-blue-600">{rank}</span>
                    </div>
                  )}
                </div>

                {/* Avatar */}
                <div className="flex-shrink-0 mr-3">
                  <img
                    className="h-10 w-10 rounded-full object-cover"
                    src={employee.photo || 'https://ui-avatars.com/api/?name=Agent&background=3b82f6&color=ffffff&size=128'}
                    alt={employee.name}
                  />
                </div>

                {/* Name and Rank */}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900">
                    {employee.name}
                  </div>
                  <div className="text-xs text-gray-500">
                    Performance Score: 0.0% | Rank: #{rank}
                  </div>
                </div>
              </div>

              {/* Metrics Row */}
              <div className="flex items-center justify-end space-x-4 text-xs">
                {teamFilter === 'PH' ? (
                  <>
                    {/* Dialers */}
                    <div className="flex items-center">
                      <i className="ri-phone-fill text-pink-500 mr-1"></i>
                      <span className="font-medium text-gray-900">{employee.dialers}</span>
                    </div>

                    {/* Total Fees */}
                    <div className="flex items-center">
                      <i className="ri-money-dollar-circle-fill text-yellow-500 mr-1"></i>
                      <span className="font-medium text-gray-900">${employee.totalFees.toLocaleString()}</span>
                    </div>

                    {/* Roadsides */}
                    <div className="flex items-center">
                      <i className="ri-roadster-fill text-red-500 mr-1"></i>
                      <span className="font-medium text-gray-900">{employee.roadsides}</span>
                    </div>

                    {/* Collections */}
                    <div className="flex items-center">
                      <i className="ri-money-dollar-box-fill text-green-500 mr-1"></i>
                      <span className="font-medium text-gray-900">${employee.collections.toLocaleString()}</span>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Policies */}
                    <div className="flex items-center">
                      <i className="ri-file-text-fill text-blue-500 mr-1"></i>
                      <span className="font-medium text-gray-900">{employee.policies}</span>
                    </div>

                    {/* Roadsides */}
                    <div className="flex items-center">
                      <i className="ri-roadster-fill text-red-500 mr-1"></i>
                      <span className="font-medium text-gray-900">{employee.roadsides}</span>
                    </div>

                    {/* Collections */}
                    <div className="flex items-center">
                      <i className="ri-money-dollar-box-fill text-green-500 mr-1"></i>
                      <span className="font-medium text-gray-900">${employee.collections.toLocaleString()}</span>
                    </div>
                  </>
                )}
              </div>

              {/* Progress Bar */}
              <div className="mt-2">
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full ${
                      rank === 1 ? 'bg-yellow-400' :
                      rank === 2 ? 'bg-gray-400' :
                      rank === 3 ? 'bg-orange-400' :
                      'bg-blue-400'
                    }`}
                    style={{
                      width: teamFilter === 'PH' 
                        ? `${(employee.dialers / maxDialers) * 100}%`
                        : `${(employee.policies / maxPolicies) * 100}%`,
                    }}
                  ></div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function RetentionMTDPage() {
  const { user } = useAuth();
  const [retentionData, setRetentionData] = useState<RetentionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isAdmin = false; // Placeholder, adjust as needed

  useEffect(() => {
    // Always fetch data, don't wait for user
    handleRefresh();
  }, []);

  const handleRefresh = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch data directly from retention_mtd_data table
      const { data, error: fetchError } = await supabase
        .from('retention_mtd_data')
        .select('*')
        .order('employee', { ascending: true });

      if (fetchError) {
        console.error('Error fetching retention MTD data:', fetchError);
        throw new Error('Failed to fetch retention MTD data');
      }

      console.log('Retention MTD data from Supabase:', data);

      // Filter out "Total" row for individual agent display
      const agentData = (data || []).filter(
        (item) => item.employee !== 'Total'
      );
      setRetentionData(agentData);
    } catch (err) {
      console.error('Error in handleRefresh:', err);
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to parse currency strings
  const parseCurrency = (value: string): number => {
    if (!value || value === '$0') return 0;
    return parseFloat(value.replace('$', '').replace(',', '')) || 0;
  };

  // Helper function to parse numeric values safely
  const parseNumeric = (value: any): number => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      // Remove any non-numeric characters except decimal points
      const cleaned = value.replace(/[^0-9.]/g, '');
      const parsed = parseFloat(cleaned);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  };

  // Calculate totals from all employees (excluding Total row)
  const filteredEmployees = retentionData.filter(item => 
    item.employee && item.employee.toLowerCase() !== 'total'
  );

  const totalCollections = filteredEmployees.reduce(
    (sum, item) => sum + parseNumeric(item.collections),
    0
  );

  const totalPolicies = filteredEmployees.reduce(
    (sum, item) => sum + parseNumeric(item.policies),
    0
  );

  const totalRoadsides = filteredEmployees.reduce(
    (sum, item) => sum + parseNumeric(item.roadsides),
    0
  );

  const totalFees = filteredEmployees.reduce(
    (sum, item) => sum + parseNumeric(item.total_fees_collected),
    0
  );

  const totalDialers = filteredEmployees.reduce(
    (sum, item) => sum + parseNumeric(item.dialers),
    0
  );

  // Calculate total dialers for Philippines Retention team
  const totalPHDialers = filteredEmployees.reduce((sum, agent) => {
    if (isPHTeamMember(agent.employee)) {
      return sum + parseNumeric(agent.dialers);
    }
    return sum;
  }, 0);

  // Get profile photo URL for each agent (fallback to generated avatar)
  const getProfilePhotoUrl = (employeeName: string) => {
    const initials = employeeName.substring(0, 2).toUpperCase();
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(
      initials
    )}&background=3b82f6&color=ffffff&size=128`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading retention MTD data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <p className="font-bold">Error Loading Data</p>
            <p>{error}</p>
          </div>
          <button
            onClick={handleRefresh}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Alias for ranking tables
  const employees = retentionData;

  return (
    <div className="min-h-screen bg-sky-400">
      <Header
        onRefresh={handleRefresh}
        isConnected={!error}
        showDashboardButton={isAdmin}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Buttons Section */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row justify-center items-center mb-6 space-y-4 lg:space-y-0">
            <div className="flex-shrink-0 flex flex-wrap justify-center gap-3 max-w-6xl">
              <Link
                to="/philippines-sales"
                className="flex-1 min-w-[140px] max-w-[180px] px-4 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <i className="ri-bar-chart-line"></i>
                <span>PH Sales MTD</span>
              </Link>

              <Link
                to="/philippines-sales-today"
                className="flex-1 min-w-[140px] max-w-[180px] px-4 py-3 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <i className="ri-calendar-line"></i>
                <span>PH Sales Today</span>
              </Link>
              
              <Link
                to="/mtd-sales"
                className="flex-1 min-w-[140px] max-w-[180px] px-4 py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <i className="ri-calendar-check-line"></i>
                <span>MTD Sales</span>
              </Link>
              
              <Link
                to="/todays-sales"
                className="flex-1 min-w-[140px] max-w-[180px] px-4 py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <i className="ri-calendar-todo-line"></i>
                <span>Today's Sales</span>
              </Link>
              
              <Link
                to="/retention-mtd"
                className="flex-1 min-w-[140px] max-w-[180px] px-4 py-3 bg-sky-600 text-white rounded-lg font-medium hover:bg-sky-700 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 flex items-center justify-center space-x-2 border-2 border-sky-300"
              >
                <i className="ri-user-heart-line"></i>
                <span>Retention MTD</span>
              </Link>

              <Link
                to="/retention-today"
                className="flex-1 min-w-[140px] max-w-[180px] px-4 py-3 bg-sky-500 text-white rounded-lg font-medium hover:bg-sky-600 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <i className="ri-user-heart-line"></i>
                <span>Retention Today</span>
              </Link>

              <Link
                to="/dashboard"
                className="flex-1 min-w-[140px] max-w-[180px] px-4 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <i className="ri-dashboard-line"></i>
                <span>Dashboard</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Retention MTD Dashboard
          </h1>
          <p className="text-gray-600 mt-2">
            Month-to-date retention performance and agent rankings
          </p>
        </div>

        {/* Summary Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <i className="ri-money-dollar-circle-line text-green-600"></i>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">
                  Total Collections
                </p>
                <p className="text-2xl font-semibold text-gray-900">
                  ${totalCollections.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className="ri-file-text-line text-blue-600"></i>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">
                  Total Policies
                </p>
                <p className="text-2xl font-semibold text-gray-900">
                  {totalPolicies}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <i className="ri-roadster-line text-purple-600"></i>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">
                  Total Roadsides
                </p>
                <p className="text-2xl font-semibold text-gray-900">
                  {totalRoadsides}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                  <i className="ri-coins-line text-orange-600"></i>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-5">
                  Total Fees
                </p>
                <p className="text-2xl font-semibold text-gray-900">
                  ${totalFees.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Retention Agents Performance MTD */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Retention Agents Performance MTD
                </h2>
                <p className="text-sm text-gray-600">
                  Individual agent performance metrics for month-to-date
                </p>
              </div>
              <div className="bg-purple-100 px-4 py-2 rounded-lg">
                <p className="text-xs text-purple-600 font-medium">Philippines Team</p>
                <p className="text-2xl font-bold text-purple-900">
                  {totalPHDialers}
                </p>
                <p className="text-xs text-purple-600">Total Dialers</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {retentionData.map((agent) => (
                <div
                  key={agent.id}
                  className="bg-gray-50 rounded-lg p-6 border border-gray-200"
                > 
                  {/* Agent Header */}
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-gray-300 mr-3">
                      <img
                        src={getProfilePhotoUrl(agent.employee)}
                        alt={agent.employee}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.parentElement!.innerHTML =
                            '<i className="ri-user-line text-white text-lg bg-blue-500 w-full h-full flex items-center justify-center"></i>';
                        }}
                      />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {agent.employee}
                      </h3>
                      <p className="text-sm text-gray-500">Retention Agent</p>
                    </div>
                  </div>

                  {/* Performance Metrics */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Collections:</span>
                      <span className="text-sm font-semibold text-green-600">
                        {agent.collections}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Policies:</span>
                      <span className="text-sm font-semibold text-blue-600">
                        {agent.policies || 0}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Roadsides:</span>
                      <span className="text-sm font-semibold text-purple-600">
                        {agent.roadsides}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Total Fees:</span>
                      <span className="text-sm font-semibold text-orange-600">
                        {agent.total_fees_collected}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Late Payments:</span>
                      <span className="text-sm font-semibold text-red-600">
                        {agent.late_payments}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">
                        Monthly Payments:
                      </span>
                      <span className="text-sm font-semibold text-indigo-600">
                        {agent.monthly_payments}
                      </span>
                    </div>

                    {/* Show Dialers for Philippines Team Members */}
                    {isPHTeamMember(agent.employee) && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Dialers:</span>
                        <span className="text-sm font-semibold text-pink-600">
                          {agent.dialers || 0}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Additional Metrics */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-gray-500">Renewals:</span>
                        <span className="ml-1 font-medium">
                          {agent.renewals}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Endorsements:</span>
                        <span className="ml-1 font-medium">
                          {agent.endorsements}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Reinstatements:</span>
                        <span className="ml-1 font-medium">
                          {agent.reinstatements}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-5">Add Veh:</span>
                        <span className="ml-1 font-medium">
                          {agent.add_veh_endo}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* USA Performance Rankings MTD */}
        <div className="mb-8">
          <RankingTable
            employees={employees}
            title="USA Performance Rankings MTD"
            teamFilter="USA"
          />
        </div>

        {/* Philippines Performance Rankings MTD */}
        <div className="mb-8">
          <RankingTable
            employees={employees}
            title="Philippines Performance Rankings MTD"
            teamFilter="PH"
          />
        </div>
      </div>
    </div>
  );
}
